import { createVNode, reactive } from 'vue';
import { message, Modal, notification } from 'ant-design-vue';
import { event_obj, useEnv, useMenuComponents, useUserStore } from 'liyu-pc-base/init-plugin';
import modalBox from 'liyu-pc-base/components/form-modal/modal-tools';
import { addMenu, import_menu } from 'liyu-pc-base/api/permission/menu-manage';
import { queryOrgList } from 'liyu-pc-base/api/sys';
import { getApiData } from 'liyu-pc-base/api/api-model';
import type { BtnListConfig, HeadBtnListConfig } from 'liyu-pc-base/api/typing';
import { cloneDeep } from 'lodash';
import { ExclamationCircleOutlined } from '@ant-design/icons-vue';

const genID = (): string => {
  const array = new Uint8Array(16); // 16 字节 = 128 位
  window.crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};
const defaultComponentTypes = [
  {
    name: '默认',
    value: '@/components/remotePages/index.vue',
    order: 0,
  },
  {
    name: '其他',
    value: null,
    order: 10,
  },
];
let _allComponentTypes = null;

const getAllComponentTypes = () => {
  if (!_allComponentTypes) {
    const res = cloneDeep(defaultComponentTypes);
    const custom_menu_components = useMenuComponents();
    for (const item of custom_menu_components) {
      res.push(item);
    }
    res.sort((a, b) => a.order - b.order);
    _allComponentTypes = res;
  }
  return _allComponentTypes;
};
const getComponentTypeByValue = value => {
  const all = getAllComponentTypes();
  const res = all.find(item => {
    return item.value === value;
  });
  if (!res) {
    return all.find(item => {
      return item.value === null;
    });
  } else {
    return res;
  }
};
export const btn_list: BtnListConfig = [
  {
    name: '修改',
    model_kind: 'custom_component',
    type: 'dropdown',
    clazz: 'primary',
    model_config: {
      title: '修改菜单',
      component: 'menuConfig',
      init_data: {
        form_type: 'edit',
      },
    },
  },
  {
    name: '新建子菜单',
    model_kind: 'custom_component',
    type: 'dropdown',
    clazz: 'primary',
    model_config: {
      title: '新建子菜单',
      component: 'menuConfig',
      init_data: {
        form_type: 'create_sub_menu',
      },
    },
  },
  {
    name: '复制菜单',
    model_kind: 'custom_component',
    type: 'dropdown',
    clazz: 'primary',
    model_config: {
      title: '复制菜单',
      component: 'menuConfig',
      init_data: {
        form_type: 'copy',
      },
    },
  },
  {
    name: '修改菜单帮助',
    model_kind: 'form',
    type: 'dropdown',
    clazz: 'primary',
    show_condition: [
      [
        {
          flag: 'menu_path',
          type: 'string',
          operator: 'not_blank_not_null',
          value: ['true'],
        },
      ],
    ],
    model_config: {
      title: '修改菜单帮助',
      api_fields: {
        menu_id: 'id',
      },
      form: {
        settings: {
          labelAlign: 'right',
          layout: 'vertical',
          col: [],
        },
        dynamicFieldList: [
          {
            children: [
              {
                type: 'upload',
                label: '文件',
                disabled: false,
                access_type: 'public',
                maxCount: 1,
                listType: 'picture',
                group: 'sys',
                span: 24,
                acceptList: ['.pdf'],
                name: 'helper_files',
                placeholder: '请选择文件上传',
                pull: 0,
                push: 0,
                offset: 0,
                queryType: 'advanced',
              },
            ],
          },
        ],
        model: {
          helper_files: [],
        },
        rules: {},
        options: {},
      },
      ok_url: '/permission/update_menu_helper',
    },
    authority: 'permission/update_menu_helper',
  },
];

export const config = {
  creatCol: [],
  updateCol: [],
};
export const head_btn_list: HeadBtnListConfig = [
  {
    name: '新建菜单',
    model_kind: 'custom_component',
    type: 'dropdown',
    clazz: 'primary',
    model_config: {
      title: '新建菜单',
      component: 'menuConfig',
      init_data: {
        form_type: 'create',
      },
    },
  },
  {
    name: '批量删除',
    clazz: 'primary',
    authority: 'permission/batch_delete_menu',
    urls: ['permission/batch_delete_menu'],
    cb: ({ selectedRowKeys }) => {
      if (!selectedRowKeys || selectedRowKeys.length <= 0) {
        notification.info({
          message: '温馨提示',
          description: '请选择要操作的数据',
        });
        return;
      }
      const userStore = useUserStore();
      const current_org = userStore.currentOrg;
      Modal.confirm({
        title: () => '是否批量删除已选中数据',
        icon: () => createVNode(ExclamationCircleOutlined),
        onOk() {
          return new Promise(resolve => {
            getApiData(
              {
                org_id: current_org.id,
                menu_id: selectedRowKeys,
              },
              '/permission/batch_delete_menu',
            )
              .then((res: any) => {
                message.success('成功删除' + res.count + '条数据');
                event_obj().emit('refresh');
              })
              .finally(() => {
                resolve(null);
              });
          }).catch(() => console.log('Oops errors!'));
        },
        onCancel() {},
      });
    },
  },
  {
    name: '批量恢复',
    clazz: 'primary',
    authority: 'permission/batch_recover_menu',
    urls: ['permission/batch_recover_menu'],
    cb: ({ selectedRowKeys }) => {
      if (!selectedRowKeys || selectedRowKeys.length <= 0) {
        notification.info({
          message: '温馨提示',
          description: '请选择要操作的数据',
        });
        return;
      }
      const userStore = useUserStore();
      const current_org = userStore.currentOrg;
      Modal.confirm({
        title: () => '是否批量恢复已选中数据',
        icon: () => createVNode(ExclamationCircleOutlined),
        onOk() {
          return new Promise(resolve => {
            getApiData(
              {
                org_id: current_org.id,
                menu_id: selectedRowKeys,
              },
              '/permission/batch_recover_menu',
            )
              .then((res: any) => {
                message.success('成功恢复' + res.count + '条数据');
                event_obj().emit('refresh');
              })
              .finally(() => {
                resolve(null);
              });
          }).catch(() => console.log('Oops errors!'));
        },
        onCancel() {},
      });
    },
  },
  {
    name: '批量添加组织',
    model_kind: 'form',
    type: 'dropdown',
    clazz: 'primary',
    model_config: {
      form: {
        settings: {
          labelAlign: 'right',
          layout: 'vertical',
          col: [],
        },
        dynamicFieldList: [
          {
            group_name: '分组名称',
            key: '4830ed2d089d3a277515df9b66f4c899',
            children: [
              {
                type: 'remoteselectpro',
                label: '组织',
                disabled: false,
                allowClear: true,
                valueKey: 'id',
                labelKeyType: 2,
                labelKey: 'name',
                labelKeyList: ['name', 'id'],
                labelSpace: '-',
                mode: 'multiple',
                placeholder: '',
                show_search: true,
                datasourceType: 'remote',
                queryType: 'advanced',
                searchConditions: [
                  [
                    {
                      searchType: 3,
                      field_name: 'is_active',
                      operator: 'eq',
                      value: [true],
                      format: 'choices',
                      class_name: 'bool',
                    },
                  ],
                ],
                simpleSearchConditions: [
                  {
                    searchType: 3,
                    field_name: 'is_active',
                    value: true,
                  },
                ],
                postUrl: '/org/query_organization_list',
                name: 'org_ids',
                key: '79cadeeacffc3aca27fdd4359c68d791',
                span: 24,
                pull: 0,
                push: 0,
                offset: 0,
                table: {
                  enable: true,
                  columns: [
                    {
                      title: '名称',
                      dataIndex: 'name',
                      cellComponent: null,
                      sorter: {
                        multiple: 1,
                      },
                      autoHeight: false,
                      autoWidth: false,
                      width: 150,
                      resizable: true,
                      ellipsis: true,
                    },
                    {
                      title: '组织类型',
                      dataIndex: 'flag',
                      cellComponent: null,
                      sorter: {
                        multiple: 1,
                      },
                      autoHeight: false,
                      autoWidth: false,
                      width: 150,
                      resizable: true,
                      ellipsis: true,
                    },
                    {
                      title: '是否可用',
                      dataIndex: 'is_active',
                      cellComponent: 'boolean',
                      filterMultiple: false,
                      contentMap: {
                        true: '可用',
                        false: '禁用',
                      },
                      colorMap: {
                        true: 'green',
                        false: 'red',
                      },
                      filters: [
                        {
                          value: 'true',
                          text: '可用',
                        },
                        {
                          value: 'false',
                          text: '禁用',
                        },
                      ],
                      autoHeight: false,
                      autoWidth: false,
                      width: 150,
                      resizable: true,
                      ellipsis: true,
                    },
                    {
                      title: '创建时间',
                      dataIndex: 'create_time',
                      cellComponent: 'datetime',
                      autoHeight: false,
                      autoWidth: false,
                      width: 150,
                      resizable: true,
                      ellipsis: true,
                    },
                    {
                      title: '更新时间',
                      dataIndex: 'update_time',
                      cellComponent: 'datetime',
                      autoHeight: false,
                      autoWidth: false,
                      width: 150,
                      resizable: true,
                      ellipsis: true,
                    },
                  ],
                  search_config: [
                    {
                      field_name: 'create_time',
                      label: '创建时间',
                      required_condition: null,
                      allowClear: true,
                      placeholder: '请输入创建时间',
                      showTime: true,
                      valueFormat: 'YYYY-MM-DD HH:mm',
                      dateFormat: 'YYYY-MM-DD HH:mm',
                      format: 'input',
                      operator_list: [
                        'between',
                        'gt',
                        'lt',
                        'eq',
                        'gte',
                        'lte',
                        'notbetween',
                        '_eq',
                        'is_or_not_null',
                        'not_eq',
                      ],
                      default_operator: 'between',
                      class_name: 'datetime',
                      autocomplete: 'off',
                      size: 'small',
                      type: 'datepicker',
                    },
                    {
                      field_name: 'update_time',
                      label: '更新时间',
                      required_condition: null,
                      allowClear: true,
                      placeholder: '请输入更新时间',
                      showTime: true,
                      valueFormat: 'YYYY-MM-DD HH:mm',
                      dateFormat: 'YYYY-MM-DD HH:mm',
                      format: 'input',
                      operator_list: [
                        'between',
                        'gt',
                        'lt',
                        'eq',
                        'gte',
                        'lte',
                        'notbetween',
                        '_eq',
                        'is_or_not_null',
                        'not_eq',
                      ],
                      default_operator: 'between',
                      class_name: 'datetime',
                      autocomplete: 'off',
                      size: 'small',
                      type: 'datepicker',
                    },
                    {
                      field_name: 'name',
                      label: '名称',
                      required_condition: null,
                      allowClear: true,
                      placeholder: '请输入名称',
                      format: 'input',
                      operator_list: [
                        'eq',
                        'contains',
                        'startswith',
                        'endswith',
                        'not_eq',
                        'is_or_not_blank_or_null',
                        'icontains',
                        'istartswith',
                        'iendswith',
                        'notcontains',
                        'notstartswith',
                        'notendswith',
                        'noticontains',
                        'notistartswith',
                        'notiendswith',
                      ],
                      default_operator: 'icontains',
                      class_name: 'str',
                      inputType: 'text',
                      autocomplete: 'off',
                      type: 'input',
                    },
                    {
                      field_name: 'flag',
                      label: '组织类型',
                      required_condition: null,
                      allowClear: true,
                      placeholder: '请输入组织类型',
                      format: 'input',
                      operator_list: [
                        'eq',
                        'contains',
                        'startswith',
                        'endswith',
                        'not_eq',
                        'is_or_not_blank_or_null',
                        'icontains',
                        'istartswith',
                        'iendswith',
                        'notcontains',
                        'notstartswith',
                        'notendswith',
                        'noticontains',
                        'notistartswith',
                        'notiendswith',
                      ],
                      default_operator: 'icontains',
                      class_name: 'str',
                      inputType: 'text',
                      autocomplete: 'off',
                      type: 'input',
                    },
                  ],
                },
                extendedFields: [],
              },
            ],
          },
        ],
        model: {
          org_ids: [],
        },
        rules: {},
        options: {},
      },
      title: '批量添加组织',
      batch_api_fields: {
        menu_ids: 'id',
      },
      ok_url: '/permission/batch_add_menu_orgs',
    },
  },
  {
    name: '批量删除组织',
    model_kind: 'form',
    type: 'dropdown',
    clazz: 'primary',
    model_config: {
      form: {
        settings: {
          labelAlign: 'right',
          layout: 'vertical',
          col: [],
        },
        dynamicFieldList: [
          {
            group_name: '分组名称',
            key: '4830ed2d089d3a277515df9b66f4c899',
            children: [
              {
                type: 'remoteselectpro',
                label: '组织',
                disabled: false,
                allowClear: true,
                valueKey: 'id',
                labelKeyType: 2,
                labelKey: 'name',
                labelKeyList: ['name', 'id'],
                labelSpace: '-',
                mode: 'multiple',
                placeholder: '',
                show_search: true,
                datasourceType: 'remote',
                queryType: 'advanced',
                searchConditions: [
                  [
                    {
                      searchType: 3,
                      field_name: 'is_active',
                      operator: 'eq',
                      value: [true],
                      format: 'choices',
                      class_name: 'bool',
                    },
                  ],
                ],
                simpleSearchConditions: [
                  {
                    searchType: 3,
                    field_name: 'is_active',
                    value: true,
                  },
                ],
                postUrl: '/org/query_organization_list',
                name: 'org_ids',
                key: '79cadeeacffc3aca27fdd4359c68d791',
                span: 24,
                pull: 0,
                push: 0,
                offset: 0,
                table: {
                  enable: true,
                  columns: [
                    {
                      title: '名称',
                      dataIndex: 'name',
                      cellComponent: null,
                      sorter: {
                        multiple: 1,
                      },
                      autoHeight: false,
                      autoWidth: false,
                      width: 150,
                      resizable: true,
                      ellipsis: true,
                    },
                    {
                      title: '组织类型',
                      dataIndex: 'flag',
                      cellComponent: null,
                      sorter: {
                        multiple: 1,
                      },
                      autoHeight: false,
                      autoWidth: false,
                      width: 150,
                      resizable: true,
                      ellipsis: true,
                    },
                    {
                      title: '是否可用',
                      dataIndex: 'is_active',
                      cellComponent: 'boolean',
                      filterMultiple: false,
                      contentMap: {
                        true: '可用',
                        false: '禁用',
                      },
                      colorMap: {
                        true: 'green',
                        false: 'red',
                      },
                      filters: [
                        {
                          value: 'true',
                          text: '可用',
                        },
                        {
                          value: 'false',
                          text: '禁用',
                        },
                      ],
                      autoHeight: false,
                      autoWidth: false,
                      width: 150,
                      resizable: true,
                      ellipsis: true,
                    },
                    {
                      title: '创建时间',
                      dataIndex: 'create_time',
                      cellComponent: 'datetime',
                      autoHeight: false,
                      autoWidth: false,
                      width: 150,
                      resizable: true,
                      ellipsis: true,
                    },
                    {
                      title: '更新时间',
                      dataIndex: 'update_time',
                      cellComponent: 'datetime',
                      autoHeight: false,
                      autoWidth: false,
                      width: 150,
                      resizable: true,
                      ellipsis: true,
                    },
                  ],
                  search_config: [
                    {
                      field_name: 'create_time',
                      label: '创建时间',
                      required_condition: null,
                      allowClear: true,
                      placeholder: '请输入创建时间',
                      showTime: true,
                      valueFormat: 'YYYY-MM-DD HH:mm',
                      dateFormat: 'YYYY-MM-DD HH:mm',
                      format: 'input',
                      operator_list: [
                        'between',
                        'gt',
                        'lt',
                        'eq',
                        'gte',
                        'lte',
                        'notbetween',
                        '_eq',
                        'is_or_not_null',
                        'not_eq',
                      ],
                      default_operator: 'between',
                      class_name: 'datetime',
                      autocomplete: 'off',
                      size: 'small',
                      type: 'datepicker',
                    },
                    {
                      field_name: 'update_time',
                      label: '更新时间',
                      required_condition: null,
                      allowClear: true,
                      placeholder: '请输入更新时间',
                      showTime: true,
                      valueFormat: 'YYYY-MM-DD HH:mm',
                      dateFormat: 'YYYY-MM-DD HH:mm',
                      format: 'input',
                      operator_list: [
                        'between',
                        'gt',
                        'lt',
                        'eq',
                        'gte',
                        'lte',
                        'notbetween',
                        '_eq',
                        'is_or_not_null',
                        'not_eq',
                      ],
                      default_operator: 'between',
                      class_name: 'datetime',
                      autocomplete: 'off',
                      size: 'small',
                      type: 'datepicker',
                    },
                    {
                      field_name: 'name',
                      label: '名称',
                      required_condition: null,
                      allowClear: true,
                      placeholder: '请输入名称',
                      format: 'input',
                      operator_list: [
                        'eq',
                        'contains',
                        'startswith',
                        'endswith',
                        'not_eq',
                        'is_or_not_blank_or_null',
                        'icontains',
                        'istartswith',
                        'iendswith',
                        'notcontains',
                        'notstartswith',
                        'notendswith',
                        'noticontains',
                        'notistartswith',
                        'notiendswith',
                      ],
                      default_operator: 'icontains',
                      class_name: 'str',
                      inputType: 'text',
                      autocomplete: 'off',
                      type: 'input',
                    },
                    {
                      field_name: 'flag',
                      label: '组织类型',
                      required_condition: null,
                      allowClear: true,
                      placeholder: '请输入组织类型',
                      format: 'input',
                      operator_list: [
                        'eq',
                        'contains',
                        'startswith',
                        'endswith',
                        'not_eq',
                        'is_or_not_blank_or_null',
                        'icontains',
                        'istartswith',
                        'iendswith',
                        'notcontains',
                        'notstartswith',
                        'notendswith',
                        'noticontains',
                        'notistartswith',
                        'notiendswith',
                      ],
                      default_operator: 'icontains',
                      class_name: 'str',
                      inputType: 'text',
                      autocomplete: 'off',
                      type: 'input',
                    },
                  ],
                },
                extendedFields: [],
              },
            ],
          },
        ],
        model: {
          org_ids: [],
        },
        rules: {},
        options: {},
      },
      title: '批量删除组织',
      batch_api_fields: {
        menu_ids: 'id',
      },
      ok_url: '/permission/batch_remove_menu_orgs',
    },
  },
  {
    name: '导出菜单',
    clazz: 'primary',
    model_kind: 'custom_component',
    model_config: {
      component: 'ExportMenu',
    },
  },
  {
    name: '导入菜单',
    clazz: 'primary',
    model_kind: 'custom_component',
    model_config: {
      component: 'ImportMenu',
    },
  },
];
